import React, { Suspense, useEffect, useRef, useState, Fragment } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from "react-helmet";

import * as THREE from 'three';

import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

import { Canvas, useLoader } from '@react-three/fiber';
import { isMobile } from 'react-device-detect';

import { useGLTF } from '@react-three/drei';

import {
    useTheme,
    Drawer,
    Box,
    Typography,
    IconButton,
    Menu,
    MenuItem
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';

import BackIcon from '../../../icons/BackIcon';
import CubesIcon from '../../../icons/CubesIcon';
import CubeIcon from '../../../icons/CubeIcon';
import StyledIconButton from '../../../partials/easyFloor/StyledIconButton';

import OpenButton from '../../../partials/OpenButton';
import DrawerHeader from '../../../styled/DrawerHeader';
import { ThemeToggle } from '../../../partials/CustomToggle';
import { LoadingScreen } from '../../../partials/LoadingScreen';
import Main from '../../../styled/Main';

import apiClient from '../../../../services/api';

import store from '../../../../store';
import { toggleMessageOpen } from '../../../../store/actions/Message';
import { useNavigate } from 'react-router-dom';

import Lights from '../partials/Lights';
import Background from '../../../partials/project/view/Background';
import Floor from '../../../partials/project/view/Floor';
import Cameras from '../../../partials/project/view/Cameras';
import Controls from '../../../partials/project/view/Controls';

import { setSelectedProject } from '../../../../store/actions/Project';
import { setElements, setMesh, setMeshId, setProjectElementId } from '../../../../store/shortcuts/Project';

import { calcCameraDistance } from '../../../../utils/Utils';
import Sidebar from './Sidebar';
import { Article } from '@mui/icons-material';
import { useSelector } from 'react-redux';

const CAMERA_DISTANCE = 100;
const SHADOWS = false;
const CULLING = false;

function getWindowDimensions() {
    const { innerWidth: width, innerHeight: height } = window;

    return { width, height };
}

const getElementPosition = (element, position) => {
    if (element.object != null) {
        return [Number(position.positionX), Number(position.positionY), Number(position.positionZ)];
    }

    return [
        Number(position.positionX) + Math.round((Math.abs(element.width) / 2)),
        Number(position.positionY) + Math.round((Math.abs(element.height) / 2)),
        Number(position.positionZ) - Math.round((Math.abs(element.depth) / 2))
    ];
};

const getElementSize = (element) => { return [element.width, element.height, element.depth] }

const FloatingButtons = (props) => {
    const {
        viewType,
        setViewType,
        open,
        handleDrawerOpen,
        handleDrawerClose
    } = props;
    const navigate = useNavigate();
    const { t } = useTranslation();

    const style = {
        position: 'absolute',
        top: '50%',
        right: '0px',
        transform: 'translateX(-50%)',
        zIndex: 9999
    };

    return (
        <Fragment>
            <Helmet>
                <title>easyBoardOnline</title>
            </Helmet>
            <div style={{ position: 'absolute', left: '10px', bottom: '10px', color: 'red', zIndex: 9999 }}>
                <ThemeToggle />
            </div>
            <div style={style}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <StyledIconButton
                        color="primary"
                        title={t('Change View')}
                        isActive={false}
                        onClick={() => {
                            if (viewType == '3D') {
                                setViewType('2D');
                            } else {
                                setViewType('3D');
                            }
                        }}
                    >
                        {viewType != '3D' ? <CubeIcon /> : <CubesIcon />}
                    </StyledIconButton>
                    <StyledIconButton
                        color="primary"
                        title={t('Show report')}
                        isActive={open}
                        onClick={() => {
                            if (open) {
                                handleDrawerClose();
                            } else {
                                handleDrawerOpen();
                            }
                        }}
                    >
                        <Article />
                    </StyledIconButton>
                </div>
            </div>
        </Fragment>
    )
};

/**
 * Calculates the center of the project
 * @param {*} param0 
 * @returns 
 */
const calcProjectCenter = ({ w = 100, h = 100, d = 100 }) => {
    const x = w / 2;
    const y = h / 2;
    const z = d / 2;

    return { x, y, z };
};

/**
 * Calculates the center of the project based on their project elements
 * 
 * @param {*} data 
 * @param {*} model 
 * @returns 
 */
const calcProjectElementsCenter = (data, model) => {
    const bBox = {
        min: { x: 0, y: 0, z: 0 },
        max: { x: 0, y: 0, z: 0 }
    };

    if (model) {
        return {
            x: Number(data.project.width) / 2,
            y: Number(data.project.height) / 2,
            z: Number(data.project.depth) / 2,
            w: Number(data.project.width),
            h: Number(data.project.height),
            d: Number(data.project.depth)
        };
    }

    const elements = data.project_elements;

    elements.forEach((element) => {
        if (element.positions) element.positions.forEach((position) => {
            const { positionX, positionY, positionZ } = position;

            const px = Number(positionX);
            const py = Number(positionY);
            const pz = Number(positionZ);

            // TODO: element sizes
            bBox.min.x = Math.min(px, bBox.min.x);
            bBox.min.y = Math.min(py, bBox.min.y);
            bBox.min.z = Math.min(pz, bBox.min.z);
            bBox.max.x = Math.max(px, bBox.max.x);
            bBox.max.y = Math.max(py, bBox.max.y);
            bBox.max.z = Math.max(pz, bBox.max.z);
        });
    });

    const w = bBox.max.x - bBox.min.x;
    const h = bBox.max.y - bBox.min.y;
    const d = bBox.max.z - bBox.min.z;

    const x = ((bBox.min.x + bBox.max.x) / 2);
    const y = ((bBox.min.y + bBox.max.y) / 2);
    const z = ((bBox.min.z + bBox.max.z) / 2);

    return { x, y, z, w, h, d };
};

const materialLambertWhite = <meshLambertMaterial attach="material" color="white" depthWrite="false" />;
const materialLambertTransparent = <meshLambertMaterial attach="material" color="white" transparent opacity={0.5} depthWrite="false" />;
const materialLambertWhite0 = <meshLambertMaterial key="0" attach="material-0" color="white" depthWrite="false" />;
const materialLambertWhite1 = <meshLambertMaterial key="1" attach="material-1" color="white" depthWrite="false" />;
const materialLambertWhite2 = <meshLambertMaterial key="2" attach="material-2" color="white" depthWrite="false" />;
const materialLambertWhite3 = <meshLambertMaterial key="3" attach="material-3" color="white" depthWrite="false" />;
const materialLambertWhite5 = <meshLambertMaterial key="5" attach="material-5" color="white" depthWrite="false" />;

const Product = ({ element, position }) => {
    if (element.image) {
        return <ProductWithImage element={element} position={position} />;
    }

    const size = getElementSize(element);
    const pos = getElementPosition(element, position);

    return (
        <Box position={pos} args={size} castShadow={SHADOWS} receiveShadow={SHADOWS}>
            {materialLambertWhite}
        </Box>
    )
};

const ProductWithImage = ({ element, position }) => {
    const size = getElementSize(element);
    const pos = getElementPosition(element, position);

    const map = useLoader(THREE.TextureLoader, element.image);

    const materials = map ? [
        materialLambertWhite0, materialLambertWhite1,
        materialLambertWhite2, materialLambertWhite3,
        <meshLambertMaterial key="4" attach="material-4" map={map} depthWrite="false" />,
        materialLambertWhite5
    ] : materialLambertWhite;

    return (
        <mesh position={pos} castShadow={SHADOWS} receiveShadow={SHADOWS}>
            <boxGeometry args={size} />
            {materials}
        </mesh>
    );
};

const Fixture = ({ element, position }) => {
    const pos = getElementPosition(element, position);
    const size = getElementSize(element);

    return (
        <Box position={pos} args={size} castShadow={SHADOWS} receiveShadow={SHADOWS}>
            {materialLambertTransparent}
        </Box>
    )
};

const AutoInstanced = ({ element }) => {
    if (element.image) {
        return <InstancedBoxWithImage element={element} />;
    }

    return <InstancedBox element={element} />;
};

const InstancedBox = ({ element, temp = new THREE.Object3D() }) => {
    const size = getElementSize(element);
    const positions = element.positions.map((position) => {
        return getElementPosition(element, position);
    });
    const count = positions.length;

    const ref = React.useRef();
    React.useEffect(() => {
        positions.forEach((pos, i) => {
            temp.position.set(pos[0], pos[1], pos[2]);
            temp.updateMatrix();
            ref.current.setMatrixAt(i, temp.matrix);
        });
        ref.current.instanceMatrix.needsUpdate = true;
        ref.current.computeBoundingBox();
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    return (
        <instancedMesh ref={ref} args={[null, null, count]} frustumCulled={false} castShadow={SHADOWS} receiveShadow={SHADOWS}>
            <boxGeometry args={size} />
            {materialLambertWhite}
        </instancedMesh>
    );
};

const InstancedBoxWithImage = ({ element, temp = new THREE.Object3D() }) => {
    const size = getElementSize(element);
    const positions = element.positions.map((position) => {
        return getElementPosition(element, position);
    });
    const count = positions.length;

    const ref = React.useRef();
    React.useEffect(() => {
        positions.forEach((pos, i) => {
            temp.position.set(pos[0], pos[1], pos[2]);
            temp.updateMatrix();
            ref.current.setMatrixAt(i, temp.matrix);
        });
        ref.current.instanceMatrix.needsUpdate = true;
        ref.current.computeBoundingBox();
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    const map = useLoader(THREE.TextureLoader, element.image);

    const materials = map ? [
        materialLambertWhite0, materialLambertWhite1,
        materialLambertWhite2, materialLambertWhite3,
        <meshLambertMaterial key="4" attach="material-4" map={map} depthWrite="false" />,
        materialLambertWhite5
    ] : materialLambertWhite;

    return (
        <instancedMesh ref={ref} args={[null, null, count]} castShadow={SHADOWS} receiveShadow={SHADOWS}>
            <boxGeometry args={size} />
            {materials}
        </instancedMesh>
    );
};

const Project = ({ elements }) => {
    return elements ? elements.map(
        (element, index) => {
            const count = element.positions.length;
            if (count > 1) {
                return <AutoInstanced key={`a${element.object_id}_${index}`} element={element} />
            } else {
                return element.positions.map((position, index) => (
                    element.is_product
                        ? <Product key={`p${element.object_id}_${index}`} element={element} position={position} />
                        : <Fixture key={`f${element.object_id}_${index}`} element={element} position={position} />
                ));
            }
        }
    ) : null;
};

const Primitive = React.memo(({ scene, position }) => {
    const copiedScene = React.useMemo(() => scene.clone(), [scene])

    return <primitive object={copiedScene} position={position} />;
});

const ObjectGroup = ({ element, center, setCenter, setDistance }) => {
    const { object, positions } = element;
    const gltf = useGLTF(object);

    return (
        <group dispose={null} castShadow={SHADOWS} receiveShadow={SHADOWS} frustumCulled={CULLING}>
            {positions.map((position, index) => {
                const pos = getElementPosition(element, position);
                return <Primitive key={index} scene={gltf.scene} position={pos} />;
            })}
        </group>
    );
};

const Objects = ({ elements }) => {
    return elements ? elements.map((element, index) => {
        const count = element.positions.length;
        if (false && count > 1) {
            return <InstancedObject key={`i${element.object_id}_${index}`} element={element} />;
        } else {
            return <ObjectGroup key={`g${element.object_id}_${index}`} element={element} />;
        }
    }) : null;
};

const backupGeometry = <boxGeometry args={[10, 10, 10]} />;
const backupMaterial = <meshLambertMaterial color="green" />;

const InstancedObject = ({ element, temp = new THREE.Object3D() }) => {
    const positions = element.positions.map((position) => {
        return getElementPosition(element, position);
    });
    const count = positions.length;
    //const url = element.object;

    //const object = useLoader(GLTFLoader, url);

    let geometry = backupGeometry;
    let material = backupMaterial;

    const ref = React.useRef();

    React.useEffect(() => {
        positions.forEach((pos, i) => {
            temp.position.set(pos[0], pos[1], pos[2]);
            temp.updateMatrix();
            ref.current.setMatrixAt(i, temp.matrix);
        });
        ref.current.instanceMatrix.needsUpdate = true;
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    return (
        <instancedMesh ref={ref} args={[null, null, count]} >
            {geometry}
            {material}
        </instancedMesh>
    );
};

function ProjectPublicPreviewV1({ data, projectElements, abort }) {
    const theme = useTheme();
    const { t } = useTranslation();

    const selectedElement = useSelector((state) => state.project.selectedMeshId);

    const [project, setProject] = useState(null);
    const [selectedProjectData, setSelectedProjectData] = useState(null);
    const [model, setModel] = useState(null);
    const [objects, setObjects] = useState([]);
    const [notObjects, setNotObjects] = useState([]);
    const [viewType, setViewType] = useState('3D');
    const [projectIsLoading, setProjectIsLoading] = useState(true);
    const [usdz, setUsdz] = useState(null);
    const [center, setCenter] = useState({ x: 0, y: 0, z: 0 });
    const [distance, setDistance] = useState(CAMERA_DISTANCE);
    const [open, setOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(500);
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
 	const [isBackgroundsModuleActive, setIsBackgroundModulesActive] = useState(false);

    const is3D = viewType === '3D';

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false)
    };

 useEffect(() => {
        if (selectedElement && isBackgroundsModuleActive) {
            setOpen(true);
        }
    }, [selectedElement]);

    useEffect(() => {
        if (windowDimensions) {
            setOpen(windowDimensions.width > 1200 ? true : false);
        }

apiClient.post('/api/modules/check', { name: 'backgrounds' }).then(response => {
            setIsBackgroundModulesActive(response?.data?.success);
        });
    }, []);

    useEffect(() => {
        if (data) {
            setSelectedProjectData(data);
        }
    }, [data]);// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (projectElements && projectElements.length != 0) {
            setElements(projectElements);
        }
    }, [projectElements]);// eslint-disable-line react-hooks/exhaustive-deps

    const componentMounted = useRef(true);

    React.useEffect(() => {
        (async () => {
            if (componentMounted.current) {
                if (selectedProjectData !== null) {
                    setProjectIsLoading(false)

                    const elements = selectedProjectData.project_elements;
                    const onlyObjects = elements.filter((item) => !!item.object);
                    const notObjects = elements.filter((item) => !item.object);
                    let center = calcProjectElementsCenter(selectedProjectData);

                    setProject(selectedProjectData.project);
                    setObjects(onlyObjects);
                    setNotObjects(notObjects);

                    if (selectedProjectData.model.gltf) {
                        if (selectedProjectData.model.gltf.optimized) {
                            setModel(selectedProjectData.model.gltf.optimized);

                            center = calcProjectElementsCenter(
                                selectedProjectData,
                                selectedProjectData.model.gltf.optimized
                            );
                        } else {
                            setModel(selectedProjectData.model.gltf.default);

                            center = calcProjectElementsCenter(
                                selectedProjectData,
                                selectedProjectData.model.gltf.default
                            );
                        }
                    }

                    setCenter(center);

                    // const distance = calcCameraDistance(size);
                    const distance = calcCameraDistance(center, true, 25);
                    setDistance(distance);

                    if (selectedProjectData.model.usdz) {
                        setUsdz(selectedProjectData.model.usdz)
                    }
                } else {
                    setProject(null);
                    setObjects(null);
                    setNotObjects(null);
                    setModel(null);
                    setUsdz(null);

                    setCenter(calcProjectCenter({}));
                    setDistance(CAMERA_DISTANCE);
                }
            }
            return () => {
                componentMounted.current = false;
            }
        })();
    }, [selectedProjectData]);

    useEffect(() => {
        if (windowDimensions.width < 650) {
            setDrawerWidth(windowDimensions.width);
        }
    }, [windowDimensions]);

    const LoadProjectGLTF = ({ center, setCenter, setDistance }) => {
        return model
            ? <ProjectGLTF
                key={`project`}
                center={center}
                setCenter={setCenter}
                setDistance={setDistance}
            />
            : null
    }

    const hoveredColor = 0x90EE90;
    const selectedColor = 0x00FF00;
    
    const getNodesColors = (gltf) => {
        const colors = [];
        Object.keys(gltf.nodes).forEach(key => {
            if (gltf.nodes[key].isMesh) {
                colors[key] = new THREE.Color(
                    gltf.nodes[key].material.color.r,
                    gltf.nodes[key].material.color.g,
                    gltf.nodes[key].material.color.b,
                );
            }
        });
        return colors;
    };
    
    const setNodesColors = (gltf, colors) => {
        Object.keys(gltf.nodes).forEach(key => {
            if (gltf.nodes[key].isMesh) {
                gltf.nodes[key].material.color.set(colors[key]);
            }
        });
    };

    const ProjectGLTF = ({ center, setCenter, setDistance }) => {
        const gltf = useLoader(GLTFLoader, model);
        const modelRef = useRef();
        const isOptimizedView = useSelector((state) => state.project.isOptimizedView);
        const selectedMeshId = useSelector((state) => state.project.selectedMeshId);
        const selectedProjectElementId = useSelector((state) => state.project.selectedProjectElementId);

        const [selectedMesh, setSelectedMesh] = useState(null);
        const [hoveredProjectElement, setHoveredProjectElement] = useState(null);
        const [originalColors, setOriginalColors] = useState([]);
        const [hoveredMesh, setHoveredMesh] = useState(null);

        useEffect(() => {
            if (center.x == 0 && center.y == 0 && center.z == 0) {
                const box = new THREE.Box3().setFromObject(gltf.scene);
                const size = new THREE.Vector3();

                box.getSize(size);

                const centerFallback = calcProjectCenter({ w: size.x, h: size.y, d: size.z });
                const distanceFallback = calcCameraDistance({ w: size.x, h: size.y, d: size.z }, true, 25);

                setCenter(centerFallback);
                setDistance(distanceFallback);
            }
        }, [gltf]);

        // Save original color of all meshes to have possibility to reset after hover or click highlight
        useEffect(() => {
            if (!gltf) return;
            const colors = getNodesColors(gltf);
            setOriginalColors(colors);
            return () => { setNodesColors(gltf, colors) }; // reset colors on dispose
        }, [gltf]);

        // Highlight meshes on hover or click
        useEffect(() => {
            if (isOptimizedView) return;
            if (originalColors) {
                setNodesColors(gltf, originalColors);
            }
            if (hoveredMesh) {
                hoveredMesh.parent.children.forEach(item => {
                    if (!item.userData.EBProjectElementId) {
                        if (item.material) {
                            item.material.color.set(hoveredColor);
                        }
                    } else if (item.userData.EBProjectElementId === hoveredProjectElement) {
                        if (item.material && item.material._listeners) delete item.material._listeners;
                        item.material = new THREE.MeshStandardMaterial(item.material);
                        item.material.color.set(hoveredColor);
                    }
                });
            }
            if (selectedMesh) {
                // if (IS_DEV) console.log(selectedMesh.parent);
                selectedMesh.parent.children.forEach(item => {
                    if (
                        (!selectedProjectElementId && item.userData.EBMeshId === `${selectedMeshId}`)
                        || !item.userData.EBProjectElementId
                    ) {
                        if (item.material) {
                            item.material.color.set(selectedColor);
                        }
                    } else if (item.userData.EBProjectElementId == selectedProjectElementId) {
                        if (item.material && item.material._listeners) delete item.material._listeners;
                        item.material = new THREE.MeshStandardMaterial(item.material);
                        item.material.color.set(selectedColor);
                    }
                });
            }
        }, [hoveredMesh, selectedMesh, selectedProjectElementId]);

        const onClickListener = (event) => {
            if (isOptimizedView) return;
            if (event.intersections.length === 0) return;

            const { userData, name } = event.intersections[0].object;

            // if (IS_DEV) console.log({ name }, userData, selectedMesh);

            if (userData?.EBMeshId) {
                const { EBMeshId, EBProjectElementId } = userData;

                // unselect on click again
                if (selectedMeshId && selectedMeshId === EBMeshId) {
                    if (selectedMesh && selectedMesh.name === name) {
                        setMeshId(null);
                        setProjectElementId(null);

                        setSelectedMesh(null);
                        setMesh(null);

                        return;
                    }
                }

                setMeshId(EBMeshId);
                setProjectElementId(EBProjectElementId);
            }

            setSelectedMesh(event.intersections[0].object);
            setMesh(event.intersections[0].object);

            event.stopPropagation();
            console.log(userData);
        }

        return (
            <group ref={modelRef}>
                <primitive object={gltf.scene} onClick={(event) => { onClickListener(event) }} />
            </group>
        );
    }

    const Scene = () => {
        if (!selectedProjectData) {
            return <></>
        }

        return (
            <>
                <Background color={theme.palette.background.default} />
                <Lights shadows={SHADOWS} />
                <Cameras perspective={is3D} position={{ ...center, z: distance }} />
                {project && <Controls rotate={is3D} project={project} target={{ ...center, z: 0 }} />}
                {is3D && !!project && <Floor color={theme.palette.border.grid} />}
                {!model ? <Project elements={notObjects} /> : null}
                <Suspense fallback={null}>
                    {model
                        ? <LoadProjectGLTF project={project} item={model} center={center} setCenter={setCenter} setDistance={setDistance} />
                        : <Objects elements={objects} />}
                </Suspense>
            </>
        );
    }

    const style = {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    };

    return (
        <Box sx={{ display: 'flex', height: '100%' }}>
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        padding: '20px'
                    },
                    padding: '0px',
                    borderLeft: '1px solid ' + theme.palette.storeDrawer.borderLeft.color
                }}
                variant="persistent"
                anchor="left"
                open={open}
                PaperProps={{
                    sx: {
                        padding: '0px',
                        position: "relative",
                        backgroundColor: theme.palette.storeDrawer.background
                    }
                }}
            >
                <DrawerHeader>
                    <Typography align="center">
                        {selectedProjectData && selectedProjectData.project.name}
                    </Typography>
                    <button
                        type="button"
                        onClick={handleDrawerClose}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            background: 'none',
                            padding: '0px',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <BackIcon />
                    </button>
                </DrawerHeader>
                <Sidebar />
            </Drawer>
            <Main open={open} drawerWidth={drawerWidth}>
                <div style={style}>
                    <Canvas shadows={SHADOWS} gl={{ preserveDrawingBuffer: true }}>
                        {!abort && <Scene />}
                    </Canvas>
                    {projectIsLoading && <LoadingScreen text="Loading Project" />}
                    <FloatingButtons
                        viewType={viewType}
                        setViewType={setViewType}
                        usdzUrl={usdz}
                        model={model}
                        selectedProjectData={selectedProjectData}
                        open={open}
                        handleDrawerOpen={handleDrawerOpen}
                        handleDrawerClose={handleDrawerClose}
                        windowDimensions={windowDimensions}
                    />
                </div>
            </Main>
        </Box>
    );
}

export default ProjectPublicPreviewV1;
